import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { setActiveCarDetails, setActiveIndex } from "../../redux/reducers/service-reducer";
import { IconButton } from "@mui/material";
import Loader from "../loder";
import { RootState } from "../../redux/reducers";
import CctvCameraIcon from "../../utils/cctvCameraIcon";
import DeviceSettingsPopover from "../DeviceSettingsDrawer";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import "../../assets/fonts/fonts.css";

interface VehicleListProps {
  fleetData?: any;
  onPressCar?: (e: any) => void;
  onClickMic?: (e: any) => void;
  deviceId?: string;
  userId?: string;
  miceStatus?: any;
  isCollapsed?: boolean;
  showSearch?: boolean;
}
const VehicleList = React.memo(({
  fleetData: fleetDataProp,
  onPressCar = (e: any) => { },
  onClickMic = (e: any) => { },
  deviceId,
  userId,
  miceStatus,
  isCollapsed,
  showSearch
}: VehicleListProps) => {
  const dispatch = useDispatch();
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const [selectedCar, setSelectedCar] = useState<any>(null);
  const [micStatus, setMicStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [fleetData, setFleetData] = useState<any>({});
  const [selectedTab, setSelectedTab] = useState(0);
  const [filteredFleetData, setFilteredFleetData] = useState<any>({});
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const activeCarDetails = useSelector((state: RootState) => state.service.activeCarDetails);
  useEffect(() => {
    if (activeCarDetails?.devicename) {
      setSelectedCar(activeCarDetails.devicename);
      const city = Object.keys(fleetData).find(city =>
        city !== "total_online" && city !== "total_offline" &&
        fleetData[city]?.fleet_devices?.some((v: any) =>
          v.devicename === activeCarDetails.devicename
        )
      );
      if (city && !expandedPanels.includes(city)) {
        setExpandedPanels(prev => [...prev, city]);
      }
    }
  }, [activeCarDetails, fleetData]);

  useEffect(() => {
    if (fleetDataProp && Object.keys(fleetDataProp).length > 0) {
      setFleetData(fleetDataProp);
      const cities = Object.keys(fleetDataProp).filter(
        (k) => k !== "total_online" && k !== "total_offline"
      );
      setExpandedPanels(cities);
      if (!activeCarDetails && cities.length > 0) {
        const firstCity = cities[0];
        const firstVehicle = fleetDataProp[firstCity]?.fleet_devices?.[0];
        if (firstVehicle) {
          dispatch(setActiveIndex(0));
          dispatch(setActiveCarDetails(firstVehicle));
          setSelectedCar(firstVehicle.devicename);
        }
      }
    }
  }, [fleetDataProp, dispatch, activeCarDetails]);

  useEffect(() => {
    setMicStatus(miceStatus);
  }, [miceStatus]);

  const handleChange = (panel: string) => () => {
    setExpandedPanels((prev: string[]) =>
      prev.includes(panel)
        ? prev.filter((p) => p !== panel)
        : [...prev, panel]
    );
  };

  const filterFleetData = (data: any, selectedTab: any, searchTerm: any) => {
    if (!data) return {};
    const filteredData: any = {};
    Object.keys(data).forEach((city) => {
      if (city === "total_online" || city === "total_offline") return;
      let fleetDevices = data[city]?.fleet_devices || [];
      if (selectedTab === 1) {
        fleetDevices = fleetDevices.filter(
          (car: any) => car.status === "online"
        );
      } else if (selectedTab === 2) {
        fleetDevices = fleetDevices.filter(
          (car: any) => car.status === "offline"
        );
      }
      if (searchTerm.trim()) {
        fleetDevices = fleetDevices.filter((car: any) =>
          car.devicename.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      if (fleetDevices.length > 0) {
        filteredData[city] = {
          ...data[city],
          fleet_devices: fleetDevices,
        };
      }
    });
    return filteredData;
  };

  useEffect(() => {
    const updatedData = filterFleetData(fleetData, selectedTab, searchTerm);
    setFilteredFleetData(updatedData);
  }, [fleetData, selectedTab, searchTerm]);

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  const handleTabChange = (newTab: any) => {
    setSelectedTab(newTab);
    setTabValue(newTab);
  };

  if (!fleetData || Object.keys(fleetData).length === 0) {
    return <Loader loading={isLoading} />;
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, vehicle: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedDevice(vehicle);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flex: ".9",
        flexDirection: "column",
        maxWidth: "xs",
        padding: "0px",
        margin: "0px",
        width: isCollapsed ? "60px" : "100%",
        fontFamily: "Avenir, sans-serif",
        borderLeft: "2px solid #e1e1e3",
        borderRight: "2px solid #e1e1e3",
        fontSize: "13px",
      }}
    >
      <DeviceSettingsPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        device={selectedDevice}
      />
      {showSearch && (
        <>
          <TextField
            label="Search vehicles"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              marginBottom: 1,
              "& .MuiOutlinedInput-root": {
                height: 36,
                borderRadius: 3,
                padding: 0,
                "& input": {
                  padding: "0 8px",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "0.875rem",
                },
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.875rem",
                top: "-8px",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </>
      )}
      <Box
        sx={{
          fontSize: "12px",
          maxHeight: "100vh",
          overflowY: "auto",
        }}
      >
        {Object.keys(filteredFleetData).map((city) => (
          <Accordion
            key={city}
            expanded={expandedPanels.includes(city)}
            onChange={handleChange(city)}
            style={{ margin: 0, padding: 0 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              sx={{
                minHeight: "10px !important",
                px: 1,
                backgroundColor: "#fff",
                "& .MuiAccordionSummary-content": {
                  margin: "4px 0",
                },
                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "10px 0",
                },
              }}
            >
              <Typography sx={{ fontWeight: 600,fontFamily: "Avenir, sans-serif",color:"#444",fontSize: isCollapsed ? "11px" : "15px" }}>
                {isCollapsed ? '' : 'Devices Group'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails style={{ margin: 0, padding: 0 }}>
              <List style={{ padding: "0px 5px" }}>
                {filteredFleetData[city].fleet_devices.map(
                  (vehicle: any, index: number) => (
                    <React.Fragment key={vehicle.deviceno}>
                      <ListItem
                        onClick={() => onPressCar(vehicle)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 1,
                          py: 0.7,
                          borderRadius: 2,
                          cursor: "pointer",

                          backgroundColor:
                            selectedCar === vehicle.devicename ? "#ead7c3" : "transparent",

                          "&:hover": {
                            backgroundColor: "#f5e6d8",
                          },
                          "& .menu-btn": {
                            opacity: 0,
                            transition: "0.2s",
                          },
                          "&:hover .menu-btn": {
                            opacity: 1,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {vehicle?.raw.device_type === "cctv" ? (
                            <CctvCameraIcon
                              color={vehicle.status === "online" ? "#ff6d00" : "#333"}
                            />
                          ) : (
                            // <DirectionsCarIcon sx={{ color: "#ff6d00", fontSize: 22 }} />
                            <CctvCameraIcon
                              color={vehicle.status === "online" ? "#ff6d00" : "#333"}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={!isCollapsed ? vehicle.devicename : ""}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontWeight: 500,
                            fontFamily: "Avenir, sans-serif",
                            padding:"0px 5px"
                          }}
                          sx={{
                            justifyContent: isCollapsed ? "center" : "space-between",
                          }}
                        />
                        {!isCollapsed && (
                          <IconButton
                            className="menu-btn"
                            size="small"
                            onClick={(e) => handleMenuClick(e, vehicle)}
                          >
                            <MoreHorizIcon fontSize="small" />
                          </IconButton>
                        )}
                      </ListItem>
                      {index <
                        filteredFleetData[city].fleet_devices.length - 1 && (
                          <Divider />
                        )}
                    </React.Fragment>
                  )
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
});
export default VehicleList;